import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Listbox, Menu } from '@headlessui/react';
import {
	CheckCircleIcon,
	CheckIcon,
	EllipsisVerticalIcon,
} from '@heroicons/react/24/solid';
import {
	ClockIcon,
	UserIcon,
	CalendarIcon,
	PencilIcon,
	PlusIcon,
	DocumentIcon,
	BookOpenIcon,
	ListBulletIcon,
	TrashIcon,
	XMarkIcon,
	ArrowPathIcon,
	ForwardIcon,
	TicketIcon,
	WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import {
	StackableModal,
	DatePicker,
	StatusBadge,
	AddAttachmentButton,
} from '../components/nextgen';
import { AvatarGenerated } from '../components/Avatar';
import { getUserFullName, classNames } from '../components/helpers/functions';
import useModal from '../hooks/useModal';
import { DangerModal } from '../components/Shell/Modal';
import {
	getTask,
	createSubtask,
	updateTask,
	deleteTask,
	skipTask,
	uploadTaskDocuments,
	removeTaskDocument,
	attachTaskSop,
	removeTaskSop,
	saveTaskSopResponses,
} from '../api/tasks';

const MODAL_NAME = 'taskDetail';

const TaskDetailModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openSopViewer } = useModal('sopViewer');
	const { setOpen: openTicketOverview } = useModal('ticketOverview');
	const { setOpen: openWorkOrderOverview } = useModal('workOrderOverview');

	const initialTask = open?.task;
	const team = open?.team || [];
	const sops = open?.sops || [];
	const onUpdate = open?.onUpdate;
	const onToggle = open?.onToggle;
	const onDelete = open?.onDelete;
	const onSkip = open?.onSkip;
	const hideToggle = open?.hideToggle || false;

	const [task, setTask] = useState(null);
	const [isEditingTitle, setIsEditingTitle] = useState(false);
	const [editedTitle, setEditedTitle] = useState('');
	const [isEditingContent, setIsEditingContent] = useState(false);
	const [editedContent, setEditedContent] = useState('');
	const [isUpdating, setIsUpdating] = useState(false);
	const [newSubtaskContent, setNewSubtaskContent] = useState('');
	const [isAddingSubtask, setIsAddingSubtask] = useState(false);
	const [showSopSelector, setShowSopSelector] = useState(false);
	const [isUploadingDocument, setIsUploadingDocument] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [showSkipConfirm, setShowSkipConfirm] = useState(false);

	// Reset state and fetch fresh data when modal opens
	useEffect(() => {
		if (open && initialTask?.id) {
			// Reset UI state
			setIsEditingTitle(false);
			setIsEditingContent(false);
			setNewSubtaskContent('');
			setIsAddingSubtask(false);
			setShowSopSelector(false);
			setIsUploadingDocument(false);
			setShowDeleteConfirm(false);
			setShowSkipConfirm(false);

			// Fetch fresh task data
			const fetchTask = async () => {
				try {
					const freshTask = await getTask(initialTask.id);
					setTask(freshTask);
					setEditedTitle(freshTask.title || '');
					setEditedContent(freshTask.content || '');
				} catch (err) {
					// Fallback to initial task if fetch fails
					console.error('Error fetching task:', err);
					setTask(initialTask);
					setEditedTitle(initialTask.title || '');
					setEditedContent(initialTask.content || '');
				}
			};
			fetchTask();
		} else if (!open) {
			setTask(null);
		}
	}, [open, initialTask?.id]);

	if (!task) return null;

	const isCompleted = task.completed;
	const isSkipped = task.skipped;
	const isTaskDone = isCompleted || isSkipped;
	const dueDate = task.dueDate || task.due_date;
	const createdAt = task.createdAt || task.created_at;
	const completedAt = task.completedAt || task.completed_at;
	const assignedToUser = task.assignedToUser || task.assigned_to_user;
	const assignedTo = task.assignedTo || task.assigned_to;
	const completedByUser = task.completedByUser || task.completed_by_user;
	const subtasks = task.subtasks || [];
	const documents = task.documents || [];
	const sop = task.sop;

	const handleToggleComplete = async () => {
		if (!onToggle) return;
		setIsUpdating(true);
		await onToggle(task.id, isCompleted);
		setTask((prev) => ({
			...prev,
			completed: !prev.completed,
			completedAt: !prev.completed ? new Date().toISOString() : null,
		}));
		setIsUpdating(false);
		setClose();
	};

	const handleTitleSave = async () => {
		if (!onUpdate || editedTitle === task.title) {
			setIsEditingTitle(false);
			return;
		}
		setIsUpdating(true);
		await onUpdate(task.id, { title: editedTitle });
		setTask((prev) => ({ ...prev, title: editedTitle }));
		setIsUpdating(false);
		setIsEditingTitle(false);
	};

	const handleContentSave = async () => {
		if (!onUpdate || editedContent === task.content) {
			setIsEditingContent(false);
			return;
		}
		setIsUpdating(true);
		await onUpdate(task.id, { content: editedContent });
		setTask((prev) => ({ ...prev, content: editedContent }));
		setIsUpdating(false);
		setIsEditingContent(false);
	};

	const handleAssigneeChange = async (userId) => {
		if (!onUpdate) return;
		setIsUpdating(true);
		await onUpdate(task.id, { assignedTo: userId || null });
		const assignedUser = userId ? team.find((m) => m.id === userId) : null;
		setTask((prev) => ({
			...prev,
			assignedTo: userId || null,
			assigned_to: userId || null,
			assignedToUser: assignedUser,
			assigned_to_user: assignedUser,
		}));
		setIsUpdating(false);
	};

	const handleDueDateChange = async (date) => {
		if (!onUpdate) return;
		setIsUpdating(true);
		await onUpdate(task.id, { dueDate: date });
		setTask((prev) => ({
			...prev,
			dueDate: date,
			due_date: date,
		}));
		setIsUpdating(false);
	};

	const handleAddSubtask = async () => {
		if (!newSubtaskContent.trim()) return;
		setIsUpdating(true);
		try {
			const newSubtask = await createSubtask(task.id, {
				content: newSubtaskContent.trim(),
			});
			setTask((prev) => ({
				...prev,
				subtasks: [...(prev.subtasks || []), newSubtask],
			}));
			setNewSubtaskContent('');
			setIsAddingSubtask(false);
		} catch (err) {
			console.error('Error creating subtask:', err);
		}
		setIsUpdating(false);
	};

	const handleToggleSubtask = async (subtask) => {
		setIsUpdating(true);
		try {
			await updateTask(subtask.id, { completed: !subtask.completed });
			setTask((prev) => ({
				...prev,
				subtasks: prev.subtasks.map((s) =>
					s.id === subtask.id ? { ...s, completed: !s.completed } : s,
				),
			}));
		} catch (err) {
			console.error('Error toggling subtask:', err);
		}
		setIsUpdating(false);
	};

	const handleRemoveSubtask = async (subtaskId) => {
		setIsUpdating(true);
		try {
			await deleteTask(subtaskId);
			setTask((prev) => ({
				...prev,
				subtasks: prev.subtasks.filter((s) => s.id !== subtaskId),
			}));
		} catch (err) {
			console.error('Error removing subtask:', err);
		}
		setIsUpdating(false);
	};

	const handleRemoveDocument = async (documentId) => {
		setIsUpdating(true);
		try {
			await removeTaskDocument(task.id, documentId);
			setTask((prev) => ({
				...prev,
				documents: prev.documents.filter((d) => d.id !== documentId),
			}));
		} catch (err) {
			console.error('Error removing document:', err);
		}
		setIsUpdating(false);
	};

	const handleAddDocuments = async (files) => {
		if (!files || files.length === 0) return;

		setIsUploadingDocument(true);
		setIsUpdating(true);
		try {
			// Separate files to upload from library document IDs
			const filesToUpload = [];
			const documentIds = [];

			for (const file of files) {
				if (file.fromLibrary && file.id) {
					// File from library - link existing document
					documentIds.push(file.id);
				} else {
					// New file to upload
					filesToUpload.push(file);
				}
			}

			// Upload files and/or link documents
			const response = await uploadTaskDocuments(
				task.id,
				filesToUpload,
				documentIds,
			);
			if (response?.documents) {
				setTask((prev) => ({
					...prev,
					documents: response.documents,
				}));
			}
		} catch (err) {
			console.error('Error adding documents:', err);
		}
		setIsUploadingDocument(false);
		setIsUpdating(false);
	};

	const handleAttachSop = async (sopId) => {
		setIsUpdating(true);
		try {
			const updatedTask = await attachTaskSop(task.id, sopId);
			setTask(updatedTask);
			setShowSopSelector(false);
		} catch (err) {
			console.error('Error attaching SOP:', err);
		}
		setIsUpdating(false);
	};

	const handleRemoveSop = async () => {
		setIsUpdating(true);
		try {
			const updatedTask = await removeTaskSop(task.id);
			setTask(updatedTask);
		} catch (err) {
			console.error('Error removing SOP:', err);
		}
		setIsUpdating(false);
	};

	const handleDelete = async () => {
		setShowDeleteConfirm(false);
		setIsUpdating(true);
		try {
			await deleteTask(task.id);
			if (onDelete) {
				onDelete(task.id);
			}
			setClose();
		} catch (err) {
			console.error('Error deleting task:', err);
		}
		setIsUpdating(false);
	};

	const handleSkip = async () => {
		setIsUpdating(true);
		try {
			await skipTask(task.id);
			if (onSkip) {
				onSkip(task.id);
			}
			setClose();
		} catch (err) {
			console.error('Error skipping task:', err);
		}
		setIsUpdating(false);
	};

	const handleUnskip = async () => {
		setIsUpdating(true);
		try {
			const updatedTask = await updateTask(task.id, {
				skipped: false,
				completed: false,
			});
			setTask(updatedTask);
			if (onUpdate) {
				onUpdate(task.id, { skipped: false, completed: false });
			}
		} catch (err) {
			console.error('Error unskipping task:', err);
		}
		setIsUpdating(false);
	};

	const handleOpenSopViewer = () => {
		const sopResponses = task.sopResponses || {};
		openSopViewer({
			sop,
			initialCompletedSteps: sopResponses.completedSteps || {},
			initialSkippedSteps: sopResponses.skippedSteps || {},
			stepInputs: sopResponses.stepInputs || {},
			readOnly: isTaskDone,
			onComplete: isTaskDone
				? null
				: async (data) => {
						try {
							const updatedTask = await saveTaskSopResponses(task.id, {
								completedSteps: data.completedSteps,
								skippedSteps: data.skippedSteps,
								stepInputs: data.stepInputs,
							});
							setTask(updatedTask);
						} catch (err) {
							console.error('Error saving SOP responses:', err);
						}
					},
		});
	};

	const completedSubtasks = subtasks.filter((s) => s.completed).length;
	const sopCompletedStepsCount = task.sopResponses?.completedSteps
		? Object.values(task.sopResponses.completedSteps).filter(Boolean).length
		: 0;

	// Check if SOP completion is required and if it's been completed
	const isSopCompletionRequired = task.requireSopCompletion && sop;
	const isSopFullyCompleted = (() => {
		if (!sop || !sop.steps?.length) return true;
		const responses = task.sopResponses || {};
		const completedSteps = responses.completedSteps || {};
		const skippedSteps = responses.skippedSteps || {};
		// Check if every step is either completed or skipped
		for (let i = 0; i < sop.steps.length; i++) {
			if (!completedSteps[i] && !skippedSteps[i]) {
				return false;
			}
		}
		return true;
	})();
	const canCompleteTask = !isSopCompletionRequired || isSopFullyCompleted;

	return (
		<>
			<StackableModal open={!!open} onClose={() => setClose()} size="lg">
				<div className="space-y-4">
					{/* Header with completion status */}
					<div className="flex items-start gap-3">
						{!hideToggle && isCompleted && (
							<button
								type="button"
								onClick={handleToggleComplete}
								disabled={
									isUpdating || !onToggle || (!isCompleted && !canCompleteTask)
								}
								className={classNames(
									'flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center transition-colors',
									isCompleted
										? 'bg-green-100 hover:bg-green-200'
										: !canCompleteTask
											? 'bg-gray-100 cursor-not-allowed'
											: 'bg-gray-100 hover:bg-indigo-100',
									onToggle && (isCompleted || canCompleteTask)
										? 'cursor-pointer'
										: 'cursor-default',
								)}
								title={
									!isCompleted && !canCompleteTask
										? 'Complete the SOP first'
										: isCompleted
											? 'Mark as incomplete'
											: 'Mark as complete'
								}
							>
								<CheckCircleIcon className="h-5 w-5 text-green-600" />
							</button>
						)}
						<div className="flex-1 min-w-0">
							{isEditingTitle ? (
								<div className="space-y-2">
									<input
										type="text"
										value={editedTitle}
										onChange={(e) => setEditedTitle(e.target.value)}
										className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
										autoFocus
									/>
									<div className="flex gap-2">
										<button
											type="button"
											onClick={handleTitleSave}
											disabled={isUpdating}
											className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
										>
											Save
										</button>
										<button
											type="button"
											onClick={() => {
												setEditedTitle(task.title || '');
												setIsEditingTitle(false);
											}}
											className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
										>
											Cancel
										</button>
									</div>
								</div>
							) : (
								<div
									className={classNames(
										'group flex items-start gap-2',
										onUpdate && !isTaskDone && 'cursor-pointer',
									)}
									onClick={() =>
										onUpdate && !isTaskDone && setIsEditingTitle(true)
									}
								>
									<h3
										className={classNames(
											'text-base font-semibold',
											task.title ? 'text-gray-900' : 'text-gray-400 italic',
										)}
									>
										{task.title || 'No title'}
									</h3>
									{onUpdate && !isTaskDone && (
										<PencilIcon className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
									)}
								</div>
							)}
						</div>
					</div>

					{/* Details */}
					<div className="border-t border-gray-100 pt-4 space-y-3">
						{/* Status */}
						<div className="flex items-center gap-3">
							<div className="text-sm flex-1 grid grid-cols-6 gap-4 flex items-center">
								<span className="text-gray-500 col-span-2">Status: </span>

								<div>
									<StatusBadge
										status={
											isCompleted
												? 'completed'
												: isSkipped
													? 'skipped'
													: 'pending'
										}
									/>
								</div>
							</div>
						</div>

						{/* Assigned to */}
						<div className="flex items-center gap-3">
							<div className="text-sm flex-1 grid grid-cols-6 gap-4 flex items-center">
								<span className="text-gray-500 col-span-2">Assigned to: </span>
								<div className="flex col-span-4">
									{onUpdate && team.length > 0 && !isTaskDone ? (
										<Listbox
											value={assignedTo || ''}
											onChange={handleAssigneeChange}
										>
											<div className="relative inline-block">
												<Listbox.Button className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer">
													{assignedToUser ? (
														<>
															<AvatarGenerated
																id={getUserFullName(assignedToUser)}
																size="xxs"
															/>
															{getUserFullName(assignedToUser)}
														</>
													) : (
														'Unassigned'
													)}
												</Listbox.Button>
												<Listbox.Options className="absolute left-0 z-10 mt-1 max-h-60 w-56 overflow-auto rounded-md bg-white py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
													<Listbox.Option
														value=""
														className={({ active, selected }) =>
															classNames(
																'cursor-pointer select-none relative py-2 pl-3 pr-9',
																active ? 'bg-indigo-50' : '',
																selected ? 'bg-indigo-100' : '',
															)
														}
													>
														{({ selected }) => (
															<div className="flex items-center">
																<span
																	className={classNames(
																		'text-gray-500 text-xs',
																		selected && 'font-semibold',
																	)}
																>
																	Unassigned
																</span>
																{selected && (
																	<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
																)}
															</div>
														)}
													</Listbox.Option>
													{team
														?.filter(
															(member) => member.active && !member.archived,
														)
														?.map((member) => (
															<Listbox.Option
																key={member.id}
																value={member.id}
																className={({ active, selected }) =>
																	classNames(
																		'cursor-pointer select-none relative py-2 pl-3 pr-9',
																		active ? 'bg-indigo-50' : '',
																		selected ? 'bg-indigo-100' : '',
																	)
																}
															>
																{({ selected }) => (
																	<div className="flex items-center text-xs">
																		<AvatarGenerated
																			id={getUserFullName(member)}
																			size="xxs"
																			className="mr-2"
																		/>
																		<span
																			className={
																				selected ? 'font-semibold' : ''
																			}
																		>
																			{member?.first_name || member?.firstName}{' '}
																			{member?.last_name || member?.lastName}
																		</span>
																		{selected && (
																			<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
																		)}
																	</div>
																)}
															</Listbox.Option>
														))}
												</Listbox.Options>
											</div>
										</Listbox>
									) : assignedToUser ? (
										<span className="inline-flex items-center gap-1.5">
											<AvatarGenerated
												id={getUserFullName(assignedToUser)}
												size="xxs"
											/>
											<span className="font-medium text-gray-900">
												{getUserFullName(assignedToUser)}
											</span>
										</span>
									) : (
										<span className="text-gray-400">Unassigned</span>
									)}
								</div>
							</div>
						</div>

						{/* Due date */}
						<div className="flex items-center gap-3">
							<div className="text-sm flex-1 grid grid-cols-6 gap-4 flex items-center">
								<span className="text-gray-500 col-span-2">Due: </span>
								<div className="col-span-4">
									{onUpdate && !isTaskDone ? (
										<DatePicker
											value={dueDate || ''}
											onChange={handleDueDateChange}
											placeholder="Set due date"
											size="sm"
											className=" !min-w-[120px]"
										/>
									) : dueDate ? (
										<>
											<span className="font-medium text-gray-900">
												{moment.utc(dueDate).format('MMM D, YYYY')}
											</span>
											{moment.utc(dueDate).isBefore(moment().startOf('day')) &&
												!isTaskDone && (
													<span className="ml-2 text-xs text-red-600 font-medium">
														Overdue
													</span>
												)}
										</>
									) : (
										<span className="text-gray-400">No due date</span>
									)}
								</div>
							</div>
						</div>

						{/* Linked Entity (Ticket or Work Order) */}
						{task.entityType === 'ticket' && task.entityId && (
							<div className="flex items-center gap-3">
								<div className="text-sm flex-1 grid grid-cols-6 gap-4 flex items-center">
									<span className="text-gray-500 col-span-2">Ticket: </span>
									<div className="col-span-4">
										<button
											type="button"
											onClick={() => {
												openTicketOverview({ id: task.entityId });
											}}
											className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 cursor-pointer transition-colors"
										>
											<TicketIcon className="h-3.5 w-3.5" />
											View Ticket
										</button>
									</div>
								</div>
							</div>
						)}
						{task.entityType === 'work_order' && task.entityId && (
							<div className="flex items-center gap-3">
								<div className="text-sm flex-1 grid grid-cols-6 gap-4 flex items-center">
									<span className="text-gray-500 col-span-2">Work Order: </span>
									<div className="col-span-4">
										<button
											type="button"
											onClick={() => {
												openWorkOrderOverview({ id: task.entityId });
											}}
											className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer transition-colors"
										>
											<WrenchScrewdriverIcon className="h-3.5 w-3.5" />
											View Work Order
										</button>
									</div>
								</div>
							</div>
						)}

						{/* Description */}
						<div className="border-t border-gray-100 pt-4">
							<div className="flex items-center justify-between mb-2">
								<span className="text-sm font-medium text-gray-700">
									Description
								</span>
								{onUpdate &&
									!isTaskDone &&
									!isEditingContent &&
									task.content && (
										<button
											type="button"
											onClick={() => setIsEditingContent(true)}
											className="text-xs text-indigo-600 hover:text-indigo-700"
										>
											Edit
										</button>
									)}

								{onUpdate && !isTaskDone && !task.content && (
									<button
										type="button"
										onClick={() => setIsEditingContent(true)}
										className="inline-flex items-center text-xs text-indigo-600 hover:text-indigo-700"
									>
										<PlusIcon className="h-3 w-3 mr-0.5" />
										Add
									</button>
								)}
							</div>
							{isEditingContent ? (
								<div className="space-y-2">
									<textarea
										value={editedContent}
										onChange={(e) => setEditedContent(e.target.value)}
										className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
										rows={3}
										placeholder="Add a description..."
										autoFocus
									/>
									<div className="flex gap-2">
										<button
											type="button"
											onClick={handleContentSave}
											disabled={isUpdating}
											className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
										>
											Save
										</button>
										<button
											type="button"
											onClick={() => {
												setEditedContent(task.content || '');
												setIsEditingContent(false);
											}}
											className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
										>
											Cancel
										</button>
									</div>
								</div>
							) : task.content ? (
								<p className="text-sm text-gray-700 whitespace-pre-wrap">
									{task.content}
								</p>
							) : (
								<p className="text-sm text-gray-400">No description</p>
							)}
						</div>

						{/* Completed info */}
						{isCompleted && completedAt && (
							<div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl my-6">
								<CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
								<div className="text-sm">
									<span className="text-green-800">Completed: </span>
									<span className="text-green-800">
										{moment(completedAt).format('MMM D, YYYY [at] h:mmA')}
										{completedByUser && (
											<span className="ml-1">
												by {getUserFullName(completedByUser)}
											</span>
										)}
									</span>
								</div>
							</div>
						)}
					</div>

					{/* SOP Section - only show if SOP attached or SOPs available to attach */}
					{(sop || (onUpdate && sops.length > 0)) && (
						<div className="border-t border-gray-100 pt-4">
							<div className="flex items-center justify-between mb-2">
								<div className="flex items-center gap-2">
									<BookOpenIcon className="h-4 w-4 text-gray-400" />
									<span className="text-sm font-medium text-gray-700">
										Standard Operating Procedure
									</span>
								</div>
								{onUpdate && !sop && sops.length > 0 && (
									<button
										type="button"
										onClick={() => setShowSopSelector(!showSopSelector)}
										className="text-xs text-indigo-600 hover:text-indigo-700"
									>
										{showSopSelector ? 'Cancel' : 'Attach SOP'}
									</button>
								)}
							</div>

							{sop ? (
								<button
									type="button"
									onClick={handleOpenSopViewer}
									className="w-full flex items-center justify-between border border-gray-300 rounded-md px-4 py-3 hover:bg-indigo-50 hover:border-indigo-100 transition-colors text-left group"
								>
									<div>
										<span className="text-sm font-medium text-indigo-900">
											{sop.name}
										</span>
										{sop.steps?.length > 0 && (
											<p className="text-xs text-gray-600 mt-0.5">
												{`Steps: ${sopCompletedStepsCount} / ${sop.steps.length}`}

												{isSopCompletionRequired && !isSopFullyCompleted && (
													<span className="ml-1 text-amber-600 font-medium">
														(Required)
													</span>
												)}
											</p>
										)}
									</div>
									<div className="flex items-center gap-2">
										<span className="text-xs text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity">
											Open SOP
										</span>
										{/*onUpdate && (
										<button
											type="button"
											onClick={(e) => {
												e.stopPropagation();
												handleRemoveSop();
											}}
											disabled={isUpdating}
											className="text-indigo-600 hover:text-indigo-800"
										>
											<XMarkIcon className="h-4 w-4" />
										</button>
									)*/}
									</div>
								</button>
							) : showSopSelector ? (
								<div className="space-y-2">
									{sops.map((s) => (
										<button
											key={s.id}
											type="button"
											onClick={() => handleAttachSop(s.id)}
											className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 border border-gray-200"
										>
											<span className="font-medium">{s.name}</span>
											{s.description && (
												<p className="text-xs text-gray-500 mt-0.5">
													{s.description}
												</p>
											)}
										</button>
									))}
								</div>
							) : null}
						</div>
					)}

					{/* Subtasks Section */}
					<div className="border-t border-gray-100 pt-4">
						<div className="flex items-center justify-between mb-2">
							<div className="flex items-center gap-2">
								<ListBulletIcon className="h-4 w-4 text-gray-400" />
								<span className="text-sm font-medium text-gray-700">
									Subtasks
									{subtasks.length > 0 && (
										<span className="ml-1 text-gray-400">
											({completedSubtasks}/{subtasks.length})
										</span>
									)}
								</span>
							</div>
							{onUpdate && !isAddingSubtask && !isTaskDone && (
								<button
									type="button"
									onClick={() => setIsAddingSubtask(true)}
									className="inline-flex items-center text-xs text-indigo-600 hover:text-indigo-700"
								>
									<PlusIcon className="h-3 w-3 mr-0.5" />
									Add
								</button>
							)}
						</div>

						{subtasks.length > 0 && (
							<div className="space-y-1 mb-2">
								{subtasks.map((subtask) => (
									<div
										key={subtask.id}
										className="group flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-50"
									>
										<button
											type="button"
											onClick={() =>
												!isTaskDone && handleToggleSubtask(subtask)
											}
											disabled={isUpdating || isTaskDone}
											className={classNames(
												'flex-shrink-0 h-4 w-4 rounded border flex items-center justify-center',
												subtask.completed
													? 'bg-green-500 border-green-500'
													: 'border-gray-300',
												!isTaskDone &&
													!subtask.completed &&
													'hover:border-indigo-500',
												isTaskDone && 'cursor-default',
											)}
										>
											{subtask.completed && (
												<CheckIcon className="h-3 w-3 text-white" />
											)}
										</button>
										<span
											className={classNames(
												'text-sm flex-1',
												subtask.completed
													? 'text-gray-400 line-through'
													: 'text-gray-700',
											)}
										>
											{subtask.content}
										</span>
										{subtask.assignedToUser && (
											<AvatarGenerated
												id={getUserFullName(subtask.assignedToUser)}
												size="xxs"
											/>
										)}
										{onUpdate && !isTaskDone && (
											<button
												type="button"
												onClick={() => handleRemoveSubtask(subtask.id)}
												disabled={isUpdating}
												className="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
											>
												<XMarkIcon className="h-4 w-4" />
											</button>
										)}
									</div>
								))}
							</div>
						)}

						{isAddingSubtask && (
							<div className="flex gap-2">
								<input
									type="text"
									value={newSubtaskContent}
									onChange={(e) => setNewSubtaskContent(e.target.value)}
									placeholder="Enter subtask..."
									className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
									autoFocus
									onKeyDown={(e) => {
										if (e.key === 'Enter') handleAddSubtask();
										if (e.key === 'Escape') {
											setIsAddingSubtask(false);
											setNewSubtaskContent('');
										}
									}}
								/>
								<button
									type="button"
									onClick={handleAddSubtask}
									disabled={isUpdating || !newSubtaskContent.trim()}
									className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
								>
									Add
								</button>
								<button
									type="button"
									onClick={() => {
										setIsAddingSubtask(false);
										setNewSubtaskContent('');
									}}
									className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
								>
									Cancel
								</button>
							</div>
						)}

						{!isAddingSubtask && subtasks.length === 0 && (
							<p className="text-sm text-gray-400">No subtasks</p>
						)}
					</div>

					{/* Documents Section */}
					<div className="border-t border-gray-100 pt-4">
						<div className="flex items-center justify-between mb-2">
							<div className="flex items-center gap-2">
								<DocumentIcon className="h-4 w-4 text-gray-400" />
								<span className="text-sm font-medium text-gray-700">
									Documents
									{documents.length > 0 && (
										<span className="ml-1 text-gray-400">
											({documents.length})
										</span>
									)}
								</span>
							</div>
							{onUpdate && !isTaskDone && (
								<AddAttachmentButton
									onUpload={handleAddDocuments}
									isUploading={isUploadingDocument}
									disabled={isUpdating}
									buttonText="Add"
									multiple={true}
								/>
							)}
						</div>

						{documents.length > 0 ? (
							<div className="space-y-1">
								{documents.map((doc) => (
									<div
										key={doc.id}
										className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50"
									>
										<div className="flex items-center gap-2">
											<DocumentIcon className="h-4 w-4 text-gray-400" />
											<a
												href={doc.location}
												target="_blank"
												rel="noopener noreferrer"
												className="text-sm text-indigo-600 hover:text-indigo-800"
											>
												{doc.name}
											</a>
											{doc.size && (
												<span className="text-xs text-gray-400">
													({Math.round(parseInt(doc.size) / 1024)} KB)
												</span>
											)}
										</div>
										{onUpdate && !isTaskDone && (
											<button
												type="button"
												onClick={() => handleRemoveDocument(doc.id)}
												disabled={isUpdating}
												className="text-gray-400 hover:text-red-600"
											>
												<TrashIcon className="h-4 w-4" />
											</button>
										)}
									</div>
								))}
							</div>
						) : (
							<p className="text-sm text-gray-400">No documents attached</p>
						)}
					</div>

					{/* User input/response if exists */}
					{task.userInput && (
						<div className="border-t border-gray-100 pt-4">
							<div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
								Response
							</div>
							<div className="text-sm text-gray-700 bg-gray-50 rounded-md px-3 py-2">
								{task.userInput}
							</div>
						</div>
					)}

					{/* Actions */}
					<div className="flex justify-between pt-4 border-t border-gray-100">
						<button
							type="button"
							onClick={() => setClose()}
							className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
						>
							Close
						</button>
						<div className="flex gap-2">
							{onToggle && !isCompleted && !isSkipped && !hideToggle && (
								<button
									type="button"
									onClick={handleToggleComplete}
									disabled={isUpdating || !canCompleteTask}
									className={classNames(
										'inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors',
										!canCompleteTask
											? 'text-gray-400 bg-gray-100 cursor-not-allowed'
											: 'text-white bg-indigo-600 hover:bg-indigo-700',
									)}
									title={
										!canCompleteTask ? 'Complete the SOP first' : undefined
									}
								>
									{'Mark Complete'}
								</button>
							)}
							{(onDelete ||
								(onSkip && !isCompleted && !isSkipped) ||
								isSkipped ||
								isCompleted) && (
								<Menu as="div" className="relative">
									<Menu.Button
										disabled={isUpdating}
										className="inline-flex items-center p-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
									>
										<EllipsisVerticalIcon className="h-5 w-5" />
									</Menu.Button>
									<Menu.Items className="absolute right-0 bottom-full mb-1 w-36 origin-bottom-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
										<div className="py-1">
											{onToggle && isCompleted && !isSkipped && (
												<Menu.Item>
													{({ active }) => (
														<button
															type="button"
															onClick={handleToggleComplete}
															className={classNames(
																'flex w-full items-center px-4 py-2 text-sm hover:bg-gray-50',
																active ? '' : 'text-gray-700',
															)}
														>
															Mark Incomplete
														</button>
													)}
												</Menu.Item>
											)}
											{isSkipped && (
												<Menu.Item>
													{({ active }) => (
														<button
															type="button"
															onClick={handleUnskip}
															className={classNames(
																'flex w-full items-center px-4 py-2 text-sm hover:bg-gray-50',
																active ? '' : 'text-gray-700',
															)}
														>
															Mark Incomplete
														</button>
													)}
												</Menu.Item>
											)}
											{onSkip && !isCompleted && !isSkipped && (
												<Menu.Item>
													{({ active }) => (
														<button
															type="button"
															onClick={() => setShowSkipConfirm(true)}
															className={classNames(
																'flex w-full items-center px-4 py-2 text-sm hover:bg-gray-50',
																active ? '' : 'text-gray-700',
															)}
														>
															Skip
														</button>
													)}
												</Menu.Item>
											)}
											{onDelete && (
												<Menu.Item>
													{({ active }) => (
														<button
															type="button"
															onClick={() => setShowDeleteConfirm(true)}
															className={classNames(
																'flex w-full items-center px-4 py-2 text-sm',
																active
																	? 'bg-red-50 text-red-700'
																	: 'text-red-600',
															)}
														>
															Delete
														</button>
													)}
												</Menu.Item>
											)}
										</div>
									</Menu.Items>
								</Menu>
							)}
						</div>
					</div>
				</div>
			</StackableModal>

			<DangerModal
				open={showDeleteConfirm}
				onClose={() => setShowDeleteConfirm(false)}
				title="Delete Task"
				confirmText="Delete"
				onConfirm={handleDelete}
			>
				Are you sure you want to delete "{task?.title || task?.content}"? This
				action cannot be undone.
			</DangerModal>

			<DangerModal
				open={showSkipConfirm}
				onClose={() => setShowSkipConfirm(false)}
				title="Skip Task"
				confirmText="Skip"
				onConfirm={handleSkip}
			>
				Are you sure you want to skip "{task?.title || task?.content}"?
			</DangerModal>
		</>
	);
};

export default TaskDetailModal;
